import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LANG_MAP = {
    ru: 'ru-RU',
    kz: 'kk-KZ',
    en: 'en-US',
};

function getBestVoice(lang) {
    const voices = window.speechSynthesis.getVoices();
    // exact match first
    let voice = voices.find(v => v.lang === lang);
    // prefix match (e.g. "ru" matches "ru-RU")
    if (!voice) voice = voices.find(v => v.lang.startsWith(lang.split('-')[0]));
    return voice || null;
}

export default function TextToSpeech() {
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' });
    const [speaking, setSpeaking] = useState(false);
    const tooltipRef = useRef(null);
    const { i18n } = useTranslation();

    useEffect(() => {
        const handleMouseUp = () => {
            setTimeout(() => {
                const selection = window.getSelection();
                const text = selection?.toString().trim();

                if (!text) {
                    setTooltip(prev => ({ ...prev, visible: false }));
                    return;
                }

                const range = selection.getRangeAt(0);
                const rect = range.getBoundingClientRect();

                setTooltip({
                    visible: true,
                    x: rect.left + rect.width / 2 + window.scrollX,
                    y: rect.top + window.scrollY - 12,
                    text,
                });
            }, 10);
        };

        const handleMouseDown = (e) => {
            if (tooltipRef.current && tooltipRef.current.contains(e.target)) return;
            setTooltip(prev => ({ ...prev, visible: false }));
            if (window.speechSynthesis.speaking) {
                window.speechSynthesis.cancel();
                setSpeaking(false);
            }
        };

        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    const handleSpeak = () => {
        if (!tooltip.text) return;

        if (window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
            setSpeaking(false);
            return;
        }

        const lang = LANG_MAP[i18n.language] || 'ru-RU';
        const utterance = new SpeechSynthesisUtterance(tooltip.text);
        utterance.lang = lang;

        // voices may not be loaded yet — wait if needed
        const trySpeak = () => {
            const voice = getBestVoice(lang);
            if (voice) utterance.voice = voice;
            utterance.onend = () => setSpeaking(false);
            utterance.onerror = () => setSpeaking(false);
            setSpeaking(true);
            window.speechSynthesis.speak(utterance);
        };

        if (window.speechSynthesis.getVoices().length === 0) {
            window.speechSynthesis.onvoiceschanged = () => {
                window.speechSynthesis.onvoiceschanged = null;
                trySpeak();
            };
        } else {
            trySpeak();
        }
    };

    if (!tooltip.visible) return null;

    return (
        <div
            ref={tooltipRef}
            onClick={handleSpeak}
            title={speaking ? 'Остановить' : 'Прочитать вслух'}
            style={{
                position: 'absolute',
                left: tooltip.x,
                top: tooltip.y,
                transform: 'translate(-50%, -100%)',
                zIndex: 9999,
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: speaking ? '#ef4444' : '#1a56db',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
                transition: 'background 0.15s',
            }}
        >
            {speaking ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <rect x="6" y="5" width="4" height="14" rx="1"/>
                    <rect x="14" y="5" width="4" height="14" rx="1"/>
                </svg>
            ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
            )}
        </div>
    );
}
